// The client list on BossDrop's own admin page (pages/ClientAccounts.js), from
// GET /auth/admin/clients (backend: server/admin/clientAccounts.js). Laid out
// like Audit Logs (pages/AuditLogs.css: the same table, row grid, pills and
// color tokens), with each client's connected tools as logos and the
// automations it has switched on. Platform admin only, like the page itself.
// BossDrop's own account (platformAdmin from the server) wears a gold crown,
// is always listed first, and can be hidden.
import React from "react";
import { Auth } from "../helpers/auth";
import ToolIcon, { TOOL_ICONS } from "./ToolIcon";
import "../pages/AuditLogs.css";
import "./ClientAccountsList.css";

// Full names for the logo tooltips and the details list.
const TOOL_NAMES = {
  slack: "Slack",
  teams: "Microsoft Teams",
  google: "Google (calendar)",
  outlook: "Microsoft 365 (Outlook)",
  asana: "Asana",
  jira: "Jira",
  linear: "Linear",
  clickup: "ClickUp",
  notion: "Notion",
  monday: "monday.com",
  hubspot: "HubSpot",
  pipedrive: "Pipedrive",
  github: "GitHub",
};

// The Control Room's own names for its automation switches, in its order.
export const CLIENT_AUTOMATIONS = [
  { key: "dailyPings", label: "Daily status pings", short: "Status pings" },
  { key: "meetings", label: "Auto-create meetings", short: "Meetings" },
  { key: "tasks", label: "Auto-create & update tasks", short: "Tasks" },
  { key: "escalations", label: "Auto-escalations", short: "Escalations" },
  { key: "execReport", label: "Executive report", short: "Exec report" },
  { key: "emailAssistant", label: "Email assistant", short: "Email assistant" },
  { key: "meetingNotes", label: "Meeting notes", short: "Meeting notes" },
];

const FILTERS = [
  { key: "all", label: "All" },
  { key: "invited", label: "Invited" },
  { key: "expired", label: "Expired" },
  { key: "joined", label: "Joined" },
  { key: "setup", label: "Set up" },
];

const OWNER_LABEL = "Platform owner (BossDrop's own account)";

// The "Hide platform owner" box, remembered in this browser only.
export const HIDE_OWNER_KEY = "bd:clientsHidePlatformOwner";

function readHideOwner() {
  try {
    return localStorage.getItem(HIDE_OWNER_KEY) === "1";
  } catch {
    return false;
  }
}

function writeHideOwner(hide) {
  try {
    if (hide) localStorage.setItem(HIDE_OWNER_KEY, "1");
    else localStorage.removeItem(HIDE_OWNER_KEY);
  } catch {}
}

function isOwner(client) {
  return client?.platformAdmin === true;
}

/** The platform owner first, then everyone else in the server's order (newest invite first). */
export function ownerFirst(clients) {
  const list = Array.isArray(clients) ? clients : [];
  return [...list.filter(isOwner), ...list.filter((c) => !isOwner(c))];
}

/** Where a client is: the status pill, its tone and the filter it falls under. */
export function clientStage(client) {
  const invite = client?.invite?.status;
  const workspace = client?.workspace?.status;
  if (workspace === "deleted") {
    return { key: "deleted", label: "Workspace deleted", tone: "neutral" };
  }
  if (invite === "accepted") {
    return workspace === "set_up"
      ? { key: "setup", label: "Set up", tone: "ok" }
      : { key: "joined", label: "Joined", tone: "info" };
  }
  if (invite === "failed") return { key: "invited", label: "Invite failed", tone: "fail" };
  if (invite === "pending") return { key: "invited", label: "Invite sent", tone: "queued" };
  if (invite === "expired") return { key: "expired", label: "Invite expired", tone: "warn" };
  return { key: "other", label: "No account", tone: "neutral" };
}

function toDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isFinite(d.getTime()) ? d : null;
}

export function formatDay(iso, now = new Date()) {
  const d = toDate(iso);
  if (!d) return "";
  const opts = { month: "short", day: "numeric" };
  if (d.getFullYear() !== now.getFullYear()) opts.year = "numeric";
  return d.toLocaleDateString(undefined, opts);
}

export function formatWhen(iso, now = new Date()) {
  const d = toDate(iso);
  if (!d) return "";
  const time = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  return `${formatDay(iso, now)}, ${time}`;
}

export function formatAgo(iso, now = new Date()) {
  const d = toDate(iso);
  if (!d) return "";
  const min = Math.round((now.getTime() - d.getTime()) / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min} min ago`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h} h ago`;
  const days = Math.round(h / 24);
  if (days < 7) return `${days} day${days === 1 ? "" : "s"} ago`;
  return formatDay(iso, now);
}

function toolName(tool) {
  return TOOL_NAMES[tool] || TOOL_ICONS[tool]?.label || String(tool || "");
}

function plural(n, one, many) {
  const count = Number(n) || 0;
  return `${count} ${count === 1 ? one : many}`;
}

function mailboxLabel(n) {
  return `${plural(n, "mailbox", "mailboxes")} tracked`;
}

/** The short line under the status pill. */
function stageNote(client, stage, now) {
  const invite = client.invite || {};
  const workspace = client.workspace || {};
  switch (stage.key) {
    case "invited":
      if (invite.status === "failed") return `Send failed ${formatDay(invite.lastFailedAt, now)}`;
      return invite.expiresAt ? `Link works until ${formatDay(invite.expiresAt, now)}` : "";
    case "expired":
      return invite.expiredAt ? `Link expired ${formatDay(invite.expiredAt, now)}` : "No live link";
    case "joined":
      return client.account?.acceptedAt
        ? `Joined ${formatDay(client.account.acceptedAt, now)}, not set up yet`
        : "Not set up yet";
    case "setup":
      return workspace.createdAt ? `Since ${formatDay(workspace.createdAt, now)}` : "";
    case "deleted":
      return workspace.deletedAt ? `Deleted ${formatDay(workspace.deletedAt, now)}` : "";
    default:
      return "";
  }
}

function SearchIcon() {
  return (
    <svg className="alIcon" viewBox="0 0 12 12" fill="none" aria-hidden="true" focusable="false">
      <circle cx="5.2" cy="5.2" r="3.4" stroke="currentColor" strokeWidth="1.3" />
      <path d="m7.8 7.8 2.5 2.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}

function ChevronIcon() {
  return (
    <svg className="alIcon alChevron" viewBox="0 0 12 12" fill="none" aria-hidden="true" focusable="false">
      <path
        d="m3.5 4.8 2.5 2.5 2.5-2.5"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CrownIcon() {
  return (
    <span className="clCrown" role="img" aria-label={OWNER_LABEL} title={OWNER_LABEL}>
      <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
        <path d="M2.2 5.6 5.3 8.3 8 3.6l2.7 4.7 3.1-2.7-1.2 6.3H3.4z" fill="currentColor" />
        <circle cx="2.2" cy="5.3" r="1.2" fill="currentColor" />
        <circle cx="8" cy="3" r="1.2" fill="currentColor" />
        <circle cx="13.8" cy="5.3" r="1.2" fill="currentColor" />
        <rect x="3.4" y="12.6" width="9.2" height="1.7" rx="0.6" fill="currentColor" />
      </svg>
    </span>
  );
}

/**
 * `refreshKey` reloads the list when it changes (the page bumps it after an
 * invite goes out). `now` fixes the clock for tests.
 */
export default function ClientAccountsList({ refreshKey = 0, now: nowProp = null }) {
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [filter, setFilter] = React.useState("all");
  const [openKeys, setOpenKeys] = React.useState(() => new Set());
  const [hideOwner, setHideOwner] = React.useState(readHideOwner);
  const mounted = React.useRef(true);

  React.useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await Auth.listClients();
      if (!mounted.current) return;
      setData({
        clients: Array.isArray(res?.clients) ? res.clients : [],
        partial: res?.partial === true,
      });
      setError("");
    } catch (err) {
      if (!mounted.current) return;
      setError(
        err?.status === 403
          ? "Only a BossDrop admin can see the client list."
          : "The client list couldn't be loaded.",
      );
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load, refreshKey]);

  const now = nowProp ? new Date(nowProp) : new Date();
  const clients = React.useMemo(() => data?.clients || [], [data]);
  const hasOwner = clients.some(isOwner);
  // A hidden owner is left out of everything below: the rows, the filter
  // counts and "Showing X of Y".
  const items = React.useMemo(
    () =>
      ownerFirst(clients)
        .filter((c) => !(hideOwner && isOwner(c)))
        .map((c) => ({ client: c, stage: clientStage(c), key: c.email })),
    [clients, hideOwner],
  );
  const counts = React.useMemo(() => {
    const out = { all: items.length };
    for (const it of items) out[it.stage.key] = (out[it.stage.key] || 0) + 1;
    return out;
  }, [items]);
  const q = query.trim().toLowerCase();
  const shown = items.filter(
    (it) =>
      (filter === "all" || it.stage.key === filter) &&
      (!q ||
        String(it.client.email || "").includes(q) ||
        String(it.client.name || "")
          .toLowerCase()
          .includes(q)),
  );

  const toggle = (key) =>
    setOpenKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const changeHideOwner = (hide) => {
    setHideOwner(hide);
    writeHideOwner(hide);
  };

  return (
    <div className="auditLogs clAccounts">
      <header className="alHeader">
        <div className="alHeader__titles">
          <h3>Clients</h3>
          <p>Every client workspace BossDrop invited, newest invite first.</p>
        </div>
        <div className="alHeader__actions">
          <button className="btn alBtn" type="button" onClick={load} disabled={loading}>
            {loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </header>

      <div className="alList">
        {clients.length ? (
          <div className="alFilters">
            <div className="alFilters__row">
              <label className="alSearch">
                <SearchIcon />
                <span className="alVisuallyHidden">Search clients</span>
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Name or email…"
                />
              </label>
              <div className="alSeg clSeg" role="group" aria-label="Filter clients by status">
                {FILTERS.map((f) => (
                  <button
                    key={f.key}
                    type="button"
                    className={`alSeg__btn${filter === f.key ? " is-active" : ""}`}
                    aria-pressed={filter === f.key}
                    onClick={() => setFilter(f.key)}
                  >
                    {f.label}
                    <span className="clSeg__count">{counts[f.key] || 0}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="alFilters__checks">
              {hasOwner ? (
                <label className="alCheck">
                  <input
                    type="checkbox"
                    className="bd-check"
                    checked={hideOwner}
                    onChange={(e) => changeHideOwner(e.target.checked)}
                  />
                  Hide platform owner
                </label>
              ) : null}
              <span className="alCount" aria-live="polite">
                Showing {shown.length} of {items.length}
              </span>
            </div>
          </div>
        ) : null}

        {error ? (
          <div className="alNotice alNotice--error" role="alert">
            <span>{error}</span>
            <button className="alLinkBtn" type="button" onClick={load}>
              Try again
            </button>
          </div>
        ) : null}

        {data?.partial ? (
          <p className="alNote">
            Some connections could not be read just now, so a client may have more tools
            connected than the logos show.
          </p>
        ) : null}

        <div className="alTable">
          <div className="alThead" aria-hidden="true">
            <span className="alThead__time">Client</span>
            <span className="alThead__type">Status</span>
            <span>Connected · Automations on</span>
            <span>Activity</span>
            <span />
          </div>

          {!data ? (
            error ? (
              <div className="alState">
                <strong>No clients to show.</strong>
              </div>
            ) : (
              <div className="alState">
                <span className="alSpinner" aria-hidden="true" />
                Loading clients…
              </div>
            )
          ) : !clients.length ? (
            <div className="alState">
              <strong>No clients yet.</strong>
              <span>Invite one above and it shows up here.</span>
            </div>
          ) : !items.length ? (
            <div className="alState">
              <strong>No other clients yet.</strong>
              <span>The platform owner is hidden.</span>
            </div>
          ) : !shown.length ? (
            <div className="alState">
              <strong>No clients match.</strong>
            </div>
          ) : (
            <ol className="alRows">
              {shown.map((it) => (
                <ClientRow
                  key={it.key}
                  client={it.client}
                  stage={it.stage}
                  open={openKeys.has(it.key)}
                  onToggle={() => toggle(it.key)}
                  now={now}
                />
              ))}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}

function ClientRow({ client, stage, open, onToggle, now }) {
  const detailsId = `cl-details-${String(client.email || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")}`;
  const invite = client.invite || {};
  const automations = client.automations;
  const switchedOn = automations ? CLIENT_AUTOMATIONS.filter((a) => automations[a.key]) : [];
  const tools = Array.isArray(client.tools) ? client.tools : [];
  const note = stageNote(client, stage, now);
  const waiting = stage.key === "invited" || stage.key === "expired";

  return (
    <li className={`alRow${open ? " alRow--open" : ""}${waiting ? " alRow--pending" : ""}`}>
      <div className="alRow__grid">
        <div className="alWhen clWho">
          <b>
            {client.name || client.email}
            {isOwner(client) ? <CrownIcon /> : null}
          </b>
          {client.name ? <span>{client.email}</span> : null}
          {invite.lastSentAt ? (
            <small>
              Invited {formatWhen(invite.lastSentAt, now)}
              {invite.sentCount > 1 ? ` (${invite.sentCount} sends)` : ""}
            </small>
          ) : null}
        </div>

        <div className="alType clStage">
          <span className={`alPill alPill--${stage.tone}`}>
            <span className="alPill__dot" aria-hidden="true" />
            {stage.label}
          </span>
          {note ? <small>{note}</small> : null}
        </div>

        <div className="alSum clSum">
          <div className="clTools">
            {tools.length ? (
              <>
                {tools.map((t) => (
                  <ToolIcon key={t} tool={t} title={toolName(t)} />
                ))}
                <span className="alVisuallyHidden">
                  Connected: {tools.map(toolName).join(", ")}
                </span>
              </>
            ) : (
              <span className="clNone">No tools connected</span>
            )}
            {client.emailTrackingMailboxes > 0 ? (
              <span className="clChip">{mailboxLabel(client.emailTrackingMailboxes)}</span>
            ) : null}
          </div>
          <div className="clChips">
            {!automations ? (
              <span className="clNone">
                {stage.key === "deleted" ? "No automations" : "Automations start after setup"}
              </span>
            ) : switchedOn.length ? (
              <>
                {switchedOn.map((a) => (
                  <span key={a.key} className="clChip clChip--on" title={a.label}>
                    {a.short}
                  </span>
                ))}
                <span className={`clChip clChip--mode clChip--${automations.mode}`}>
                  {automations.mode === "auto" ? "Automated" : "Manual approval"}
                </span>
              </>
            ) : (
              <span className="clNone">All automations off</span>
            )}
          </div>
        </div>

        <div className="alOut clAct">
          <b>
            {client.lastActivityAt
              ? `BossDrop ran ${formatAgo(client.lastActivityAt, now)}`
              : "No BossDrop runs yet"}
          </b>
          <small>
            {client.account?.lastSignInAt
              ? `Signed in ${formatAgo(client.account.lastSignInAt, now)}`
              : "Never signed in"}
          </small>
        </div>

        <div className="alMore">
          <button
            type="button"
            className="alDetailsBtn"
            aria-expanded={open}
            aria-controls={detailsId}
            onClick={onToggle}
            title={open ? "Hide details" : "Show details"}
          >
            <span className="alDetailsBtn__label">Details</span>
            <ChevronIcon />
          </button>
        </div>
      </div>

      {open ? <ClientDetails id={detailsId} client={client} now={now} /> : null}
    </li>
  );
}

function ClientDetails({ id, client, now }) {
  const invite = client.invite || {};
  const account = client.account || {};
  const workspace = client.workspace || {};
  const automations = client.automations;
  const tools = Array.isArray(client.tools) ? client.tools : [];
  return (
    <div
      className="clDetails"
      id={id}
      role="region"
      aria-label={`Details for ${client.name || client.email}`}
    >
      <section className="clCard">
        <h4>Invite and sign-in</h4>
        <dl>
          <dt>Last sent</dt>
          <dd>{invite.lastSentAt ? formatWhen(invite.lastSentAt, now) : "No send recorded"}</dd>
          {invite.sentCount > 1 ? (
            <>
              <dt>First sent</dt>
              <dd>{formatWhen(invite.firstSentAt, now)}</dd>
            </>
          ) : null}
          <dt>Sends</dt>
          <dd>{Number(invite.sentCount) || 0}</dd>
          {invite.expiresAt ? (
            <>
              <dt>Link works until</dt>
              <dd>{formatWhen(invite.expiresAt, now)}</dd>
            </>
          ) : null}
          {invite.expiredAt ? (
            <>
              <dt>Link expired</dt>
              <dd>{formatWhen(invite.expiredAt, now)}</dd>
            </>
          ) : null}
          {invite.lastFailedAt ? (
            <>
              <dt>Last send failed</dt>
              <dd>{formatWhen(invite.lastFailedAt, now)}</dd>
            </>
          ) : null}
          <dt>Accepted</dt>
          <dd>
            {invite.status === "accepted"
              ? account.acceptedAt
                ? formatWhen(account.acceptedAt, now)
                : "Yes"
              : "Not yet"}
          </dd>
          <dt>Last sign-in</dt>
          <dd>{account.lastSignInAt ? formatWhen(account.lastSignInAt, now) : "Never"}</dd>
          <dt>Two-step sign-in</dt>
          <dd>{account.mfa ? "On" : "Off"}</dd>
        </dl>
      </section>

      <section className="clCard">
        <h4>Workspace</h4>
        <dl>
          <dt>Set up</dt>
          <dd>{workspace.createdAt ? formatWhen(workspace.createdAt, now) : "Not yet"}</dd>
          {workspace.status === "set_up" && workspace.updatedAt ? (
            <>
              <dt>Last saved</dt>
              <dd>{formatWhen(workspace.updatedAt, now)}</dd>
            </>
          ) : null}
          {workspace.deletedAt ? (
            <>
              <dt>Deleted</dt>
              <dd>{formatWhen(workspace.deletedAt, now)}</dd>
            </>
          ) : null}
          <dt>Team Directory</dt>
          <dd>{plural(workspace.teamDirectory, "person", "people")}</dd>
          <dt>Hub access</dt>
          <dd>{plural(workspace.hubMembers, "teammate", "teammates")}</dd>
        </dl>
      </section>

      <section className="clCard">
        <h4>Connected</h4>
        {tools.length ? (
          <ul className="clList">
            {tools.map((t) => (
              <li key={t}>
                <ToolIcon tool={t} title={toolName(t)} />
                <span>{toolName(t)}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="clNone">No tools connected yet.</p>
        )}
        {client.emailTrackingMailboxes > 0 ? (
          <p className="clSub">Email tracking: {mailboxLabel(client.emailTrackingMailboxes)}.</p>
        ) : null}
      </section>

      <section className="clCard">
        <h4>Automations</h4>
        {automations ? (
          <>
            <p className="clSub">
              {automations.mode === "auto"
                ? "Automated: BossDrop acts on its own, except where a setting asks for approval."
                : "Manual approval: every action waits for approval first."}
            </p>
            <ul className="clList">
              {CLIENT_AUTOMATIONS.map((a) => (
                <li key={a.key}>
                  <span
                    className={`clDot${automations[a.key] ? " clDot--on" : ""}`}
                    aria-hidden="true"
                  />
                  <span>{a.label}</span>
                  <span className="clOnOff">{automations[a.key] ? "On" : "Off"}</span>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <p className="clNone">Shown once the workspace is set up.</p>
        )}
      </section>
    </div>
  );
}
